import { Routes } from '@angular/router';
import { AppLayout } from './app/layout/component/app.layout';
import { Notfound } from './app/pages/notfound/notfound';
import { Role } from '@/core/models/auth.interface';
import { WmDashboardComponent } from '@/pages/dashboards/wealth-manager-dashboard/wealth-manager-dashboard.component';
import { CDashboardComponent } from '@/pages/dashboards/client-dashboard/client-dashboard.component';
import { authGuard } from '@/core/guards/auth.guard';
import { roleGuard } from '@/core/guards/role.guard';
import { complianceRequestsGuard } from '@/core/guards/compliance-requests.guard';
import { dealPreferencesPromptGuard } from '@/core/guards/deal-preferences-prompt.guard';
import { rootRedirectGuard } from '@/core/guards/root-redirect.guard';
import { AdminDashboardComponent } from '@/pages/dashboards/admin-dashboard/admin-dashboard.component';
import { RelationshipManagerDashboardComponent } from '@/pages/dashboards/relationship-manager-dashboard/relationship-manager-dashboard.component';
import { SharedDealWithWMFilter, SharedDealWithRMFilter, SharedDealWithClientFilter } from '@/pages/shared-deals/data-access/shared-deals.enum';
import { sharedDealsClientClosingChildRoutes, sharedDealsClientClosingSimpleRoute } from '@/pages/shared-deals/data-access/shared-deals.config';
import { DealsFilterAdmin, DealsFilterRM, DealsFilterWM, DealsFilterClient } from '@/pages/deals/data-access/deals.enum';
import {RegistrationFilter } from '@/pages/registrations/data-access/registrations.enum';

export const appRoutes: Routes = [
    {
        path: '',
        component: AppLayout,
        canActivate: [authGuard, complianceRequestsGuard, dealPreferencesPromptGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [rootRedirectGuard],
                children: []
            },
            {
                path: '',
                children: [
                    { path: 'uikit', loadChildren: () => import('./app/pages/uikit/uikit.routes') },
                    { path: 'shared', loadChildren: () => import('./app/shared/ui-components/ui-component.routes') },

                    // { path: 'documentation', component: Documentation },
                    {
                        path: 'user-profile',
                        loadComponent: () => import('./app/pages/user-profile/user-profile.component').then((c) => c.UserProfileComponent),
                        title: 'User Profile | BettaAlpha'
                    },
                    {
                        path: 'edit-deal-preferences',
                        loadComponent: () => import('./app/pages/user-profile/deal-preferences-editor/deal-preferences-editor.component').then((c) => c.DealPreferencesEditorComponent),
                        title: 'Edit Deal Preferences | BettaAlpha'
                    },
                    {
                        path: 'account-settings',
                        loadComponent: () => import('./app/pages/account-settings/account-settings.component').then((c) => c.AccountSettingsComponent),
                        title: 'Account Setting | BettaAlpha'
                    },
                    {
                        path: 'notifications',
                        loadComponent: () => import('./app/pages/notifications/notifications.component').then((c) => c.NotificationsComponent),
                        title: 'Notifications | BettaAlpha'
                    },
                    {
                        path: 'questions',
                        loadComponent: () => import('./app/pages/questions/questions.component').then((c) => c.QuestionsComponent),
                        title: 'Questions | BettaAlpha'
                    },
                    {
                        path: 'feedback',
                        loadComponent: () => import('./app/pages/feedback/feedback.component').then((c) => c.FeedbackComponent),
                        title: 'Feedback | BettaAlpha'
                    },
                ]
            },
            {
                path: 'admin',
                canMatch: [roleGuard],
                data: {
                    roles: [Role.SUPER_ADMIN, Role.ADMIN_DEAL_USER_PREPARER, Role.ADMIN_DEAL_USER_APPROVER, Role.ADMIN_CLIENT_USERS]
                },
                children: [
                    {
                        path: 'dashboard',
                        component: AdminDashboardComponent,
                        title: 'Dashboard | BettaAlpha'
                    },
                    {
                        path: 'deal-management',
                        children: [
                            {
                                path: 'deals',
                                canMatch: [roleGuard],
                                data: { roles: [Role.SUPER_ADMIN, Role.ADMIN_DEAL_USER_PREPARER, Role.ADMIN_DEAL_USER_APPROVER] },
                                title: 'Opportunities | Opportunity Management | BettaAlpha',
                                children: [
                                    {
                                        path: '',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.ALL },
                                        title: 'All Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'draft',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.DRAFT },
                                        title: 'Draft Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'diligence-commercials',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.DILIGENCE_COMMERCIALS },
                                        title: 'Diligence & Commercials | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'investment-committee',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.INVESTMENT_COMMITTEE },
                                        title: 'Investment Committee | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'live',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.LIVE },
                                        title: 'Live Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'closing',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.CLOSING },
                                        title: 'Closing | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'completed',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.COMPLETED },
                                        title: 'Completed Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'declined',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.DECLINED },
                                        title: 'Declined Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'missed',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.MISSED },
                                        title: 'Missed Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'watchlist',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterAdmin.WATCHLIST_DEALS },
                                        title: 'Watchlist | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'create',
                                        loadComponent: () => import('./app/pages/deals/create-deal/create-deal.component').then((c) => c.CreateDealComponent),
                                        title: 'Create Deal | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'edit/:id',
                                        loadComponent: () => import('./app/pages/deals/create-deal/create-deal.component').then((c) => c.CreateDealComponent),
                                        title: 'Edit Deal | Opportunity Management | BettaAlpha'
                                    }
                                ]
                            },
                            {
                                path: 'shared-opportunities',
                                children: [
                                    {
                                        path: 'wm',
                                        children: [
                                            {
                                                path: '',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.ALL },
                                                title: 'Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'open',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.OPEN },
                                                title: 'Open | Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'review',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.REVIEW },
                                                title: 'Review | Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'live',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.LIVE },
                                                title: 'Live | Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'closing',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.CLOSING },
                                                title: 'Closing | Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'completed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.COMPLETED },
                                                title: 'Completed | Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'declined',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.DECLINED },
                                                title: 'Declined | Shared with WM | BettaAlpha'
                                            },
                                            {
                                                path: 'missed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithWMFilter.MISSED },
                                                title: 'Missed | Shared with WM | BettaAlpha'
                                            }
                                        ]
                                    },
                                    {
                                        path: 'rm',
                                        children: [
                                            {
                                                path: '',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.ALL },
                                                title: 'Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'live',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.LIVE },
                                                title: 'Live | Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'closing',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.CLOSING },
                                                title: 'Closing | Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'completed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.COMPLETED },
                                                title: 'Completed | Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'missed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.MISSED },
                                                title: 'Missed | Shared with RM | BettaAlpha'
                                            }
                                        ]
                                    },
                                    {
                                        path: 'client',
                                        children: [
                                            {
                                                path: '',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.ALL },
                                                title: 'Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'live',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.LIVE },
                                                title: 'Live | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'expressed-interest',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.EXPRESSED_INTEREST },
                                                title: 'Expressed Interest | Shared with Client | BettaAlpha'
                                            },
                                            // Admin only: closing/all, closing/subscription, closing/capital-call, etc.
                                            {
                                                path: 'closing',
                                                children: sharedDealsClientClosingChildRoutes,
                                            },
                                            {
                                                path: 'completed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.COMPLETED },
                                                title: 'Completed | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'declined',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.DECLINED },
                                                title: 'Declined | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'missed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.MISSED },
                                                title: 'Missed | Shared with Client | BettaAlpha'
                                            }
                                            ,
                                            {
                                                path: 'withdrawn',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.WITHDRAWN },
                                                title: 'Withdrawn | Shared with Client | BettaAlpha'
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'deal-details/:id',
                        loadComponent: () => import('./app/pages/deals/components/deal-details/deals-deal-details.component').then((c) => c.DealsDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'shared-opportunities/deal-details/:id',
                        loadComponent: () => import('./app/pages/my-portfolio/deal-details/deal-details.component').then((c) => c.DealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'shared-opportunities/:view/deal-details/:id',
                        loadComponent: () => import('./app/pages/shared-deals/components/deal-details/shared-deal-details.component').then((c) => c.SharedDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'investments',
                        canMatch: [roleGuard],
                        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN_DEAL_USER_PREPARER, Role.ADMIN_DEAL_USER_APPROVER] },
                        loadComponent: () => import('./app/pages/investments/investments.component').then((c) => c.InvestmentsComponent),
                        title: 'Investments | BettaAlpha'
                    },
                    {
                        path: 'users',
                        children: [
                            {
                                path: 'admin-users',
                                canMatch: [roleGuard],
                                data: { roles: [Role.SUPER_ADMIN] },
                                loadComponent: () => import('./app/pages/admin-users/admin-users.component').then((c) => c.AdminUsersComponent),
                                title: 'Admin Users | Users | BettaAlpha'
                            },
                            {
                                path: 'wealth-managers',
                                loadComponent: () => import('./app/pages/wealth-managers/wealth-managers.component').then((c) => c.WealthManagersComponent),
                                title: 'Wealth Managers | Users | BettaAlpha'
                            },
                            {
                                path: 'relationship-managers',
                                loadComponent: () => import('./app/pages/relationship-managers/relationship-managers.component').then((c) => c.RelationshipManagersComponent),
                                title: 'Relationship Managers | Users | BettaAlpha'
                            },
                            {
                                path: 'clients',
                                loadComponent: () => import('./app/pages/clients/clients.component').then((c) => c.ClientsComponent),
                                title: 'Clients | Users | BettaAlpha'
                            }
                        ]
                    },
                    {
                        path: 'registrations',
                        canMatch: [roleGuard],
                        data: {
                            roles: [Role.SUPER_ADMIN, Role.ADMIN_CLIENT_USERS]
                        },
                        children: [
                            {
                                path: '',
                                redirectTo: 'bettaalpha-customer/all',
                                pathMatch: 'full'
                            },
                            {
                                path: 'bettaalpha-customer',
                                children: [
                                    { path: '', redirectTo: 'all', pathMatch: 'full' },
                                    {
                                        path: 'all',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.ALL },
                                        title: 'All | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'pending',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PENDING },
                                        title: 'Pending | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'kyc',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.KYC },
                                        title: 'KYC | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'due-diligence',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DUE_DILIGENCE },
                                        title: 'Due Diligence | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'partnership-agreement',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PARTNERSHIP_AGREEMENT },
                                        title: 'Partnership Agreement | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'approved',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.APPROVED },
                                        title: 'Approved | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'declined',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DECLINED },
                                        title: 'Declined | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'removed',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.REMOVED },
                                        title: 'Removed | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'not-currently-serviceable',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.NOT_CURRENTLY_SERVICEABLE },
                                        title: 'Not Currently Serviceable | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'prospect',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PROSPECT },
                                        title: 'Prospect | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'prospect/new',
                                        loadComponent: () => import('./app/pages/registrations/create-prospect/create-prospect.component').then((c) => c.CreateProspectComponent),
                                        title: 'Add Prospect | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'details/:externalId',
                                        loadComponent: () => import('./app/pages/registrations/registration-details/registration-details.component').then((c) => c.RegistrationDetailsComponent),
                                        title: 'Registration Details | BettaAlpha Customer | Registrations | BettaAlpha'
                                    }
                                ]
                            },
                            {
                                path: 'product-owner',
                                children: [
                                    { path: '', redirectTo: 'all', pathMatch: 'full' },
                                    {
                                        path: 'all',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.ALL },
                                        title: 'All | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'pending',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PENDING },
                                        title: 'Pending | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'kyc',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.KYC },
                                        title: 'KYC | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'due-diligence',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DUE_DILIGENCE },
                                        title: 'Due Diligence | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'commercial-agreement',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.COMMERCIAL_AGREEMENT },
                                        title: 'Commercial Agreement | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'approved',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.APPROVED },
                                        title: 'Approved | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'declined',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DECLINED },
                                        title: 'Declined | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'removed',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.REMOVED },
                                        title: 'Removed | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'details/:externalId',
                                        loadComponent: () => import('./app/pages/registrations/registration-details/registration-details.component').then((c) => c.RegistrationDetailsComponent),
                                        title: 'Registration Details | Product Owner | Registrations | BettaAlpha'
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'specific-deal-requests',
                        loadComponent: () =>
                            import('./app/pages/specific-deal-requests/specific-deal-requests.component').then((c) => c.SpecificDealRequestsComponent),
                        title: 'Specific Deal Requests | BettaAlpha'
                    },
                    {
                        path: 'demo-requests',
                        loadComponent: () =>
                            import('./app/pages/demo-requests/demo-requests.component').then((c) => c.DemoRequestsComponent),
                        title: 'Demo Requests | BettaAlpha'
                    },
                    {
                        path: 'learning-hub',
                        children: [
                            {
                                path: '',
                                loadComponent: () =>
                                    import('./app/pages/learning-hub/learning-hub.component').then(
                                        (c) => c.LearningHubComponent,
                                    ),
                                title: 'Learning Hub | BettaAlpha',
                            },
                            {
                                path: 'manage-sections',
                                canMatch: [roleGuard],
                                data: { roles: [Role.SUPER_ADMIN] },
                                loadComponent: () =>
                                    import(
                                        './app/pages/learning-hub/manage-sections/learning-hub-manage-sections.component'
                                    ).then((c) => c.LearningHubManageSectionsComponent),
                                title: 'Manage Sections | BettaAlpha',
                            },
                        ],
                    },
                    {
                        path: 'subscriptions',
                        loadComponent: () =>
                            import('./app/pages/subscriptions/subscriptions.component').then((c) => c.SubscriptionsComponent),
                        title: 'Subscriptions | BettaAlpha'
                    },
                    {
                        path: 'lets-talk-requests',
                        loadComponent: () =>
                            import('./app/pages/lets-talk-requests/lets-talk-requests.component').then((c) => c.LetsTalkRequestsComponent),
                        title: "Let's Talk Requests | BettaAlpha"
                    },
                    {
                        path: 'new-tasks',
                        children: [
                            {
                                path: '',
                                redirectTo: 'website/demo-requests',
                                pathMatch: 'full',
                            },
                            {
                                path: ':scope/:subscope',
                                loadComponent: () =>
                                    import('./app/pages/new-tasks/new-tasks.component').then((c) => c.NewTasksComponent),
                                title: 'New Tasks | BettaAlpha',
                            },
                        ],
                    },
                ]
            },
            {
                path: 'wm',
                canMatch: [roleGuard],
                data: { roles: [Role.WEALTH_MANAGER_SUPER_USER, Role.WEALTH_MANAGER_DEALS, Role.WEALTH_MANAGER_USERS] },
                children: [
                    {
                        path: 'dashboard',
                        component: WmDashboardComponent,
                        title: 'Dashboard | BettaAlpha'
                    },
                    {
                        path: 'deal-management',
                        children: [
                            {
                                path: 'deals',
                                title: 'Opportunities | Opportunity Management | BettaAlpha',
                                children: [
                                    {
                                        path: '',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.ALL },
                                        title: 'All Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'open',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.OPEN },
                                        title: 'Open Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'review',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.REVIEW },
                                        title: 'Review Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'live',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.LIVE },
                                        title: 'Live Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'closing',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.CLOSING },
                                        title: 'Closing | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'completed',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.COMPLETED },
                                        title: 'Completed Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'declined',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.DECLINED },
                                        title: 'Declined Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'missed',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterWM.MISSED },
                                        title: 'Missed Opportunities | Opportunity Management | BettaAlpha'
                                    }
                                ]
                            },
                            {
                                path: 'shared-opportunities',
                                canMatch: [roleGuard],
                                data: { roles: [Role.WEALTH_MANAGER_SUPER_USER, Role.WEALTH_MANAGER_USERS] },
                                children: [
                                    {
                                        path: 'rm',
                                        children: [
                                            {
                                                path: '',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.ALL },
                                                title: 'Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'live',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.LIVE },
                                                title: 'Live | Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'closing',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.CLOSING },
                                                title: 'Closing | Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'completed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.COMPLETED },
                                                title: 'Completed | Shared with RM | BettaAlpha'
                                            },
                                            {
                                                path: 'missed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithRMFilter.MISSED },
                                                title: 'Missed | Shared with RM | BettaAlpha'
                                            }
                                        ]
                                    },
                                    {
                                        path: 'client',
                                        children: [
                                            {
                                                path: '',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.ALL },
                                                title: 'Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'live',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.LIVE },
                                                title: 'Live | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'expressed-interest',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.EXPRESSED_INTEREST },
                                                title: 'Expressed Interest | Shared with Client | BettaAlpha'
                                            },
                                            // WM · Shared with Clients: simple closing only (no phase sub-routes)
                                            sharedDealsClientClosingSimpleRoute,
                                            {
                                                path: 'completed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.COMPLETED },
                                                title: 'Completed | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'declined',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.DECLINED },
                                                title: 'Declined | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'missed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.MISSED },
                                                title: 'Missed | Shared with Client | BettaAlpha'
                                            }
                                            ,
                                            {
                                                path: 'withdrawn',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.WITHDRAWN },
                                                title: 'Withdrawn | Shared with Client | BettaAlpha'
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'deal-details/:id',
                        loadComponent: () => import('./app/pages/deals/components/deal-details/deals-deal-details.component').then((c) => c.DealsDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        // DocuSign redirects here after embedded NDA signing.
                        path: 'deal-details/:id/nda/return',
                        loadComponent: () => import('./app/pages/deals/components/nda-signing-return/nda-signing-return.component').then((c) => c.NdaSigningReturnComponent),
                        title: 'Signing NDA | BettaAlpha'
                    },
                    {
                        path: 'shared-opportunities/:view/deal-details/:id',
                        loadComponent: () => import('./app/pages/shared-deals/components/deal-details/shared-deal-details.component').then((c) => c.SharedDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'users',
                        canMatch: [roleGuard],
                        data: { roles: [Role.WEALTH_MANAGER_SUPER_USER, Role.WEALTH_MANAGER_USERS] },
                        children: [
                            {
                                path: 'wealth-managers',
                                canMatch: [roleGuard],
                                data: { roles: [Role.WEALTH_MANAGER_SUPER_USER] },
                                loadComponent: () => import('./app/pages/wealth-managers/wealth-managers.component').then((c) => c.WealthManagersComponent),
                                title: 'Wealth Managers | Users | BettaAlpha'
                            },
                            {
                                path: 'relationship-managers',
                                canMatch: [roleGuard],
                                data: { roles: [Role.WEALTH_MANAGER_SUPER_USER, Role.WEALTH_MANAGER_USERS] },
                                loadComponent: () => import('./app/pages/relationship-managers/relationship-managers.component').then((c) => c.RelationshipManagersComponent),
                                title: 'Relationship Managers | Users | BettaAlpha'
                            },
                            {
                                path: 'clients',
                                canMatch: [roleGuard],
                                data: { roles: [Role.WEALTH_MANAGER_SUPER_USER] },
                                loadComponent: () => import('./app/pages/clients/clients.component').then((c) => c.ClientsComponent),
                                title: 'Clients | Users | BettaAlpha'
                            }
                        ]
                    },
                    {
                        path: 'registrations',
                        children: [
                            {
                                path: '',
                                redirectTo: 'bettaalpha-customer/all',
                                pathMatch: 'full'
                            },
                            {
                                path: 'bettaalpha-customer',
                                children: [
                                    { path: '', redirectTo: 'all', pathMatch: 'full' },
                                    {
                                        path: 'all',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.ALL },
                                        title: 'All | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'not-currently-serviceable',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.NOT_CURRENTLY_SERVICEABLE },
                                        title: 'Not Currently Serviceable | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'pending',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PENDING },
                                        title: 'Pending | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'kyc',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.KYC },
                                        title: 'KYC | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'due-diligence',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DUE_DILIGENCE },
                                        title: 'Due Diligence | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'partnership-agreement',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PARTNERSHIP_AGREEMENT },
                                        title: 'Partnership Agreement | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'approved',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.APPROVED },
                                        title: 'Approved | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'declined',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DECLINED },
                                        title: 'Declined | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'removed',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.REMOVED },
                                        title: 'Removed | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'prospect',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PROSPECT },
                                        title: 'Prospect | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'prospect/new',
                                        loadComponent: () => import('./app/pages/registrations/create-prospect/create-prospect.component').then((c) => c.CreateProspectComponent),
                                        title: 'Add Prospect | BettaAlpha Customer | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'details/:externalId',
                                        loadComponent: () => import('./app/pages/registrations/registration-details/registration-details.component').then((c) => c.RegistrationDetailsComponent),
                                        title: 'Registration Details | BettaAlpha Customer | Registrations | BettaAlpha'
                                    }
                                ]
                            },
                            {
                                path: 'product-owner',
                                children: [
                                    { path: '', redirectTo: 'all', pathMatch: 'full' },
                                    {
                                        path: 'all',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.ALL },
                                        title: 'All | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'pending',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.PENDING },
                                        title: 'Pending | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'kyc',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.KYC },
                                        title: 'KYC | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'due-diligence',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DUE_DILIGENCE },
                                        title: 'Due Diligence | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'commercial-agreement',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.COMMERCIAL_AGREEMENT },
                                        title: 'Commercial Agreement | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'approved',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.APPROVED },
                                        title: 'Approved | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'declined',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.DECLINED },
                                        title: 'Declined | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'removed',
                                        loadComponent: () => import('./app/pages/registrations/registrations.component').then((c) => c.RegistrationsComponent),
                                        data: { filter: RegistrationFilter.REMOVED },
                                        title: 'Removed | Product Owner | Registrations | BettaAlpha'
                                    },
                                    {
                                        path: 'details/:externalId',
                                        loadComponent: () => import('./app/pages/registrations/registration-details/registration-details.component').then((c) => c.RegistrationDetailsComponent),
                                        title: 'Registration Details | Product Owner | Registrations | BettaAlpha'
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'new-tasks',
                        children: [
                            {
                                path: '',
                                redirectTo: 'deals/deal-approval',
                                pathMatch: 'full',
                            },
                            {
                                path: ':scope/:subscope',
                                loadComponent: () =>
                                    import('./app/pages/new-tasks/new-tasks.component').then((c) => c.NewTasksComponent),
                                title: 'New Tasks | BettaAlpha',
                            },
                        ],
                    },
                    {
                        path: 'learning-hub',
                        children: [
                            {
                                path: '',
                                loadComponent: () =>
                                    import('./app/pages/learning-hub/learning-hub.component').then(
                                        (c) => c.LearningHubComponent,
                                    ),
                                title: 'Learning Hub | BettaAlpha',
                            },
                            {
                                path: 'manage-sections',
                                canMatch: [roleGuard],
                                data: { roles: [Role.SUPER_ADMIN] },
                                loadComponent: () =>
                                    import(
                                        './app/pages/learning-hub/manage-sections/learning-hub-manage-sections.component'
                                    ).then((c) => c.LearningHubManageSectionsComponent),
                                title: 'Manage Sections | BettaAlpha',
                            },
                        ],
                    },
                ]
            },
            {
                path: 'rm',
                canMatch: [roleGuard],
                data: { roles: [Role.RELATIONSHIP_MANAGER] },
                children: [
                    {
                        path: 'dashboard',
                        component: RelationshipManagerDashboardComponent,
                        title: 'Dashboard | BettaAlpha'
                    },
                    {
                        path: 'deal-details/:id',
                        loadComponent: () => import('./app/pages/deals/components/deal-details/deals-deal-details.component').then((c) => c.DealsDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'deal-management',
                        children: [
                            {
                                path: 'deals',
                                title: 'Opportunities | Opportunity Management | BettaAlpha',
                                children: [
                                    {
                                        path: '',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterRM.ALL },
                                        title: 'All Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'live',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterRM.LIVE },
                                        title: 'Live Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'closing',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterRM.CLOSING },
                                        title: 'Closing | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'completed',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterRM.COMPLETED },
                                        title: 'Completed Opportunities | Opportunity Management | BettaAlpha'
                                    },
                                    {
                                        path: 'missed',
                                        loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                        data: { filter: DealsFilterRM.MISSED },
                                        title: 'Missed Opportunities | Opportunity Management | BettaAlpha'
                                    }
                                ]
                            },
                            {
                                path: 'shared-opportunities',
                                children: [
                                    { path: '', redirectTo: 'client', pathMatch: 'full' },
                                    {
                                        path: 'client',
                                        children: [
                                            {
                                                path: '',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.ALL },
                                                title: 'Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'live',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.LIVE },
                                                title: 'Live | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'expressed-interest',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.EXPRESSED_INTEREST },
                                                title: 'Expressed Interest | Shared with Client | BettaAlpha'
                                            },
                                            // RM · Shared with Clients: simple closing only (no phase sub-routes)
                                            sharedDealsClientClosingSimpleRoute,
                                            {
                                                path: 'completed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.COMPLETED },
                                                title: 'Completed | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'declined',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.DECLINED },
                                                title: 'Declined | Shared with Client | BettaAlpha'
                                            },
                                            {
                                                path: 'missed',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.MISSED },
                                                title: 'Missed | Shared with Client | BettaAlpha'
                                            }
                                            ,
                                            {
                                                path: 'withdrawn',
                                                loadComponent: () => import('./app/pages/shared-deals/shared-deals.component').then((c) => c.SharedDealsComponent),
                                                data: { filter: SharedDealWithClientFilter.WITHDRAWN },
                                                title: 'Withdrawn | Shared with Client | BettaAlpha'
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'clients',
                        loadComponent: () => import('./app/pages/clients/clients.component').then((c) => c.ClientsComponent),
                        title: 'Clients | Users | BettaAlpha'
                    },
                    {
                        path: 'shared-opportunities/:view/deal-details/:id',
                        loadComponent: () => import('./app/pages/shared-deals/components/deal-details/shared-deal-details.component').then((c) => c.SharedDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'new-tasks',
                        children: [
                            {
                                path: '',
                                redirectTo: 'deals/deal-subscription',
                                pathMatch: 'full',
                            },
                            {
                                path: ':scope/:subscope',
                                loadComponent: () =>
                                    import('./app/pages/new-tasks/new-tasks.component').then((c) => c.NewTasksComponent),
                                title: 'New Tasks | BettaAlpha',
                            },
                        ],
                    },
                    {
                        path: 'learning-hub',
                        children: [
                            {
                                path: '',
                                loadComponent: () =>
                                    import('./app/pages/learning-hub/learning-hub.component').then(
                                        (c) => c.LearningHubComponent,
                                    ),
                                title: 'Learning Hub | BettaAlpha',
                            },
                            {
                                path: 'manage-sections',
                                canMatch: [roleGuard],
                                data: { roles: [Role.SUPER_ADMIN] },
                                loadComponent: () =>
                                    import(
                                        './app/pages/learning-hub/manage-sections/learning-hub-manage-sections.component'
                                    ).then((c) => c.LearningHubManageSectionsComponent),
                                title: 'Manage Sections | BettaAlpha',
                            },
                        ],
                    },
                ]
            },
            {
                path: 'client',
                canMatch: [roleGuard],
                data: { roles: [Role.CLIENT] },
                children: [
                    {
                        path: 'dashboard',
                        component: CDashboardComponent,
                        title: 'Dashboard | BettaAlpha'
                    },
                    {
                        path: 'deals',
                        title: 'Deals | BettaAlpha',
                        children: [
                            {
                                path: '',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.ALL },
                                title: 'All Opportunities | BettaAlpha'
                            },
                            {
                                path: 'live',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.LIVE },
                                title: 'Live Opportunities | BettaAlpha'
                            },
                            {
                                path: 'expressed-interest',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.EXPRESSED_INTEREST },
                                title: 'Expressed Interest | BettaAlpha'
                            },
                            {
                                path: 'closing',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.CLOSING },
                                title: 'Closing Opportunities | BettaAlpha'
                            },
                            {
                                path: 'completed',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.COMPLETED },
                                title: 'Completed Opportunities | BettaAlpha'
                            },
                            {
                                path: 'declined',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.DECLINED },
                                title: 'Declined Opportunities | BettaAlpha'
                            },
                            {
                                path: 'missed',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.MISSED },
                                title: 'Missed Opportunities | BettaAlpha'
                            },
                            {
                                path: 'withdrawn',
                                loadComponent: () => import('./app/pages/deals/deals.component').then((c) => c.DealsComponent),
                                data: { filter: DealsFilterClient.WITHDRAWN },
                                title: 'Withdrawn Opportunities | BettaAlpha'
                            }
                        ]
                    },
                    {
                        path: 'deal-details/:id',
                        loadComponent: () => import('./app/pages/deals/components/deal-details/deals-deal-details.component').then((c) => c.DealsDealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        // DocuSign redirects here after the client signs a deal NDA (id = client deal).
                        path: 'deal-details/:id/nda/return',
                        loadComponent: () => import('./app/pages/deals/components/nda-signing-return/nda-signing-return.component').then((c) => c.NdaSigningReturnComponent),
                        data: { portal: 'client' },
                        title: 'Signing NDA | BettaAlpha'
                    },
                    {
                        path: 'my-portfolio',
                        loadComponent: () => import('./app/pages/my-portfolio/my-portfolio.component').then((c) => c.MyPortfolioComponent),
                        title: 'My Portfolio | BettaAlpha'
                    },
                    {
                        path: 'my-portfolio/deal-details/:id',
                        loadComponent: () => import('./app/pages/my-portfolio/deal-details/deal-details.component').then((c) => c.DealDetailsComponent),
                        title: 'Deal Details | BettaAlpha'
                    },
                    {
                        path: 'notifications',
                        loadComponent: () => import('./app/pages/notifications/notifications.component').then((c) => c.NotificationsComponent),
                        title: 'Notifications | BettaAlpha'
                    },
                    {
                        path: 'learning-hub',
                        children: [
                            {
                                path: '',
                                loadComponent: () =>
                                    import('./app/pages/learning-hub/learning-hub.component').then(
                                        (c) => c.LearningHubComponent,
                                    ),
                                title: 'Learning Hub | BettaAlpha',
                            },
                            {
                                path: 'manage-sections',
                                canMatch: [roleGuard],
                                data: { roles: [Role.SUPER_ADMIN] },
                                loadComponent: () =>
                                    import(
                                        './app/pages/learning-hub/manage-sections/learning-hub-manage-sections.component'
                                    ).then((c) => c.LearningHubManageSectionsComponent),
                                title: 'Manage Sections | BettaAlpha',
                            },
                        ],
                    },
                    {
                        path: 'activity',
                        redirectTo: 'notifications',
                        pathMatch: 'full'
                    },
                    {
                        path: 'new-tasks',
                        children: [
                            {
                                path: '',
                                redirectTo: 'deals/deal-subscription',
                                pathMatch: 'full',
                            },
                            {
                                path: ':scope/:subscope',
                                loadComponent: () =>
                                    import('./app/pages/new-tasks/new-tasks.component').then((c) => c.NewTasksComponent),
                                title: 'New Tasks | BettaAlpha',
                            },
                        ],
                    },
                ]
            }
        ]
    },
    // { path: 'landing', component: Landing },
    { path: 'notfound', component: Notfound },
    { path: 'auth', loadChildren: () => import('./app/pages/auth/auth.routes') },
    {
        path: 'onboarding',
        loadComponent: () => import('./app/pages/registrations/components/onboarding-landing/onboarding-landing.component').then((c) => c.OnboardingLandingComponent),
        title: 'Onboarding | BettaAlpha'
    },
    {
        path: 'wm-onboarding-kyc',
        loadComponent: () => import('./app/pages/registrations/components/onboarding/wealth-manager-onboarding.components').then((c) => c.WealthManagersOnboardingComponent),
        title: 'Wealth Manager Onboarding KYC | BettaAlpha'
    },
    {
        path: 'wm-onboarding',
        loadComponent: () => import('./app/pages/registrations/components/onboarding/wealth-manager-onboarding.components').then((c) => c.WealthManagersOnboardingComponent),
        title: 'Wealth Manager Onboarding | BettaAlpha'
    },
    {
        path: 'registration',
        loadComponent: () => import('./app/pages/registrations/registration/registration.component').then((c) => c.RegistrationComponent),
        title: 'Wealth Manager Registration | BettaAlpha'
    },
    {
        path: 'po-onboarding-kyc',
        loadComponent: () => import('./app/pages/registrations/components/product-owner-onboarding/product-owner-onboarding.component').then((c) => c.ProductOwnerOnboardingComponent),
        title: 'Product Owner Onboarding KYC | BettaAlpha'
    },
    {
        path: 'po-onboarding',
        loadComponent: () => import('./app/pages/registrations/components/product-owner-onboarding/product-owner-onboarding.component').then((c) => c.ProductOwnerOnboardingComponent),
        title: 'Product Owner Onboarding | BettaAlpha'
    },
    {
        path: 'privacy-policy',
        loadComponent: () => import('./app/pages/privacy-policy/privacy-policy.component').then((c) => c.PrivacyPolicyComponent),
        title: 'Privacy Policy | BettaAlpha'
    },
    {
        path: 'terms-and-conditions',
        loadComponent: () => import('./app/pages/terms-and-conditions/terms-and-conditions.component').then((c) => c.TermsAndConditionsComponent),
        title: 'Terms & Conditions | BettaAlpha'
    },
    {
        path: 'acceptable-use-policy',
        loadComponent: () => import('./app/pages/acceptable-use-policy/acceptable-use-policy.component').then((c) => c.AcceptableUsePolicyComponent),
        title: 'Acceptable Use Policy | BettaAlpha'
    },
    {
        path: 'security',
        loadComponent: () => import('./app/pages/security/security.component').then((c) => c.SecurityComponent),
        title: 'Security | BettaAlpha'
    },
    { path: '**', redirectTo: '/notfound' }
];
